// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Form } from "react-bootstrap";
import InputNumber from "rc-input-number";

import { ActionType, IAction, IActionChangeTariff, raiseAction } from "actions";
import { TariffAssociation } from "graphql/api";
import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { tabClickHandler, toDropdownItems } from "guiutils";
import { IState } from "states/states";
import { ITariff } from "states/ocppstates";

import IconDelete from "icons/iconDelete";
import IconSave from "icons/iconSave";
import Dropdown from "./Dropdown";

type ITariffAssociationsExtProps = {
  chargerId?: number;
  rfidId?: number;
};

type ITariffAssociationsProps = {
  dataState: DataState;
  tariffs: ITariff[];
  associations: TariffAssociation[];
};

const mapStateToProps = (state: IState, ownProps: ITariffAssociationsExtProps) => {
  let associations: TariffAssociation[];
  if (isSet(ownProps.chargerId)) {
    associations =
      state.ocpp.charger.find((curItem) => curItem.fk_charger === ownProps.chargerId)?.tariffs ??
      [];
  } else {
    associations = state.ocpp.rfid.find((curItem) => curItem.id === ownProps.rfidId)?.tariffs ?? [];
  }
  const props: ITariffAssociationsProps = {
    dataState: state.gui.modal.dataState,
    tariffs: state.ocpp.siteTariff,
    associations,
  };
  return props;
};

interface ITariffAssociationsState {
  isAdding: boolean;
  tariffId?: number;
  priority?: number;
}

interface ITariffAssociationsDispatch {
  onAdd: (tariffId: number, priority: number | undefined) => void;
  onDelete: (tariffId: number) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: ITariffAssociationsExtProps,
) => {
  const dispatchProps: ITariffAssociationsDispatch = {
    onAdd: (tariffId: number, priority: number | undefined) => {
      const payload: IActionChangeTariff = {
        id: tariffId,
        chargerId: ownProps.chargerId,
        rfidId: ownProps.rfidId,
        priority: priority ?? 0,
        isActive: true,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_REQ, payload));
    },
    onDelete: (tariffId: number) => {
      const payload: IActionChangeTariff = {
        id: tariffId,
        chargerId: ownProps.chargerId,
        rfidId: ownProps.rfidId,
        isActive: false,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select tariff",
  },
  msgDelete: {
    id: "remove tariff from this charging card",
  },
  msgSave: {
    id: "save tariff to this charging card",
  },
});

class TariffAssociationsComponent extends React.Component<
  ITariffAssociationsProps & ITariffAssociationsDispatch,
  ITariffAssociationsState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: ITariffAssociationsState = {
    isAdding: false,
  };

  public componentDidUpdate() {
    // after saving a new associations, ensure that the input state is cleared
    if (
      this.state.tariffId &&
      this.props.associations.map((curAss) => curAss.tariffId).includes(this.state.tariffId)
    ) {
      this.setState({ isAdding: false, tariffId: undefined });
    }
  }

  public render() {
    if (this.props.tariffs.filter((curItem) => isSet(curItem.isActive)).length === 0) {
      // no tariffs available
      return (
        <div id="tariff-associations">
          <FormattedMessage id="None" />
        </div>
      );
    }

    const tariffItems = this.props.associations.map((curAss) => {
      const tariff = this.props.tariffs.find((curTariff) => curTariff.id === curAss.tariffId);
      return (
        <tr key={curAss.tariffId}>
          <td>{tariff?.name}</td>
          <td className="center">{curAss.priority}</td>
          <td
            tabIndex={0}
            onKeyDown={tabClickHandler}
            className="img-link"
            onClick={
              this.props.dataState !== DataState.SAVE_REQ ?
                (_evt) => this.props.onDelete(curAss.tariffId)
              : undefined
            }
            title={this.context.formatMessage(messages.msgDelete)}
          >
            <IconDelete />
          </td>
        </tr>
      );
    });
    return (
      <div id="tariff-associations">
        {tariffItems.length > 0 || this.state.isAdding ?
          <table>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="Tariff" />
                </th>
                <th>
                  <FormattedMessage id="Priority" />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tariffItems}
              {this.state.isAdding && this.renderNewAssociation()}
            </tbody>
          </table>
        : <span className="me-2">
            <FormattedMessage id="Site tariffs are used." />
          </span>
        }
        {!this.state.isAdding && (
          <Button variant="outline-success" onClick={(_evt) => this.setState({ isAdding: true })}>
            <FormattedMessage id="Add Tariff" />
          </Button>
        )}
      </div>
    );
  }

  private renderNewAssociation() {
    // set default options, if not overwritten by parent component
    const tariffIds = this.props.tariffs
      .filter(
        (curItem) =>
          // global tariffs not associated to this site have isActive === undefined are shall not be shown
          isSet(curItem.isActive) &&
          // show only tariffs not yet associated
          !this.props.associations.map((curAss) => curAss.tariffId).includes(curItem.id),
      )
      .map((curItem) => curItem.id);
    const tariffItems = toDropdownItems(
      tariffIds,
      "Tariff",
      (_name, value) => this.setState({ tariffId: value }),
      (tariffId) =>
        this.props.tariffs.find((curItem) => curItem.id === tariffId)?.name ?? "<illegal tariff",
    );

    return (
      <tr key="new-tariff">
        {tariffItems.length > 0 ?
          <>
            <td>
              <Dropdown
                className="me-1"
                items={tariffItems}
                caption={this.context.formatMessage(messages.selectCaption)}
                curItem={
                  this.props.tariffs.find((curItem) => curItem.id === this.state.tariffId)
                    ?.name ?? <FormattedMessage id="Select Tariff" />
                }
              />
            </td>
            <td>
              <InputNumber
                id="maxPower"
                type="number"
                style={{ width: 10 }}
                min={0}
                value={this.state.priority ?? 0}
                onChange={(number) => {
                  if (isSet(number)) {
                    this.setState({
                      priority: number,
                    });
                  }
                }}
              />
            </td>
            <td
              tabIndex={0}
              onKeyDown={tabClickHandler}
              className="img-link"
              onClick={
                isSet(this.state.tariffId) && this.props.dataState !== DataState.SAVE_REQ ?
                  (_evt) => this.props.onAdd(this.state.tariffId!, this.state.priority)
                : undefined
              }
              title={this.context.formatMessage(messages.msgSave)}
            >
              {isSet(this.state.tariffId) && <IconSave />}
            </td>
          </>
        : <td colSpan={3}>
            <FormattedMessage id="No more tariffs available." />
          </td>
        }
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TariffAssociationsComponent);
